import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { BUTTON_SIZES, GhostButton, SecondaryButton } from 'ui/button';
import { InputSearch } from 'ui/inputs/inputSearch';
import { TabsGroup, IndexTabsList, IndexTabTrigger } from 'ui/tabsGroup';
import { FolderClosed } from 'ui/icons/action/FolderClosed';
import { TableToggle } from 'ui/indexPages/tables/tableToggle';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { useTranslations } from '../../hooks/useTranslations';
import { useFilterContext } from '../../contexts/FilterContext';
import { useViewContext, CREATIVE_LIBRARY_VIEWS } from '../../contexts/ViewContext';
import { FILE_TYPE_FILTERS } from './constants';
import { useCreativeLibraryFolderModals } from 'modules/creativeLibrary/hooks/useCreativeLibraryModals';
import {
  flattenFilters,
  getPrefixToSelectedFiltersMap,
} from 'modules/creativeLibrary/utils/filter';
import { CreativeLibraryFilterPrefix } from 'modules/creativeLibrary/types';
import { CreativeLibraryFilterMenu } from './CreativeLibraryIndexFilterMenu';
import { usePaginationContext } from 'utils/contexts/pagination';

const ControlsContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing.plusThreeQuarter};
  > * {
    flex: 0 0 auto;
    margin-left: ${spacing.half};
  }
`;

const RightMeta = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const WideInputSearch = styled(InputSearch)`
  width: ${pxToRem(300)};
`;

const StyledTableToggle = styled(TableToggle)`
  height: ${spacing.doubleHalf};
  width: ${spacing.doubleHalf};
  padding: ${spacing.quarter};
`;

export const CreativeLibraryControls = () => {
  const { t } = useTranslations();
  const {
    fileType,
    setFileType,
    searchQuery,
    setSearchQuery,
    updatedByUserId,
    setUpdatedByUserId,
    createdByUserId,
    setCreatedByUserId,
    fileSize,
    setFileSize,
    clearFilters,
  } = useFilterContext();
  const [searchInputValue, setSearchInputValue] = useState(searchQuery);
  const { activeView, setActiveView, hasManageBaseTemplatesPermission } = useViewContext();
  const modals = useCreativeLibraryFolderModals();
  const { setCurrentPage } = usePaginationContext();

  const flatSelectedFilters = useMemo(
    () => flattenFilters([fileType, fileSize, createdByUserId, updatedByUserId]),
    [fileType, updatedByUserId, createdByUserId, fileSize],
  );

  useDebouncedEffect(
    () => {
      setCurrentPage(1);
      setSearchQuery(searchInputValue);
    },
    [searchInputValue],
    {
      skipEffectOnMount: true,
      debounceTimeout: 500,
    },
  );

  const handleFilterChange = (filters: string[][]) => {
    setCurrentPage(1);
    const selectedFilters = getPrefixToSelectedFiltersMap(filters);
    setCreatedByUserId((selectedFilters[CreativeLibraryFilterPrefix.Creator] as string[]) || []);
    setUpdatedByUserId((selectedFilters[CreativeLibraryFilterPrefix.ModifiedBy] as string[]) || []);
    setFileType((selectedFilters[CreativeLibraryFilterPrefix.FileType] as string[]) || []);
    setFileSize((selectedFilters[CreativeLibraryFilterPrefix.FileSize] as string[]) || []);
  };

  const handleClearFilters = () => {
    setCurrentPage(1);
    clearFilters();
  };

  return (
    <ControlsContainer data-test="creative-library-table-controls">
      <div data-test="left-section-controls">
        <TabsGroup
          data-test="creative-library-table-file-type-filter"
          value={fileType.length === 1 ? fileType[0] : FILE_TYPE_FILTERS[0].value}
          onValueChange={(value) => {
            setCurrentPage(1);
            setFileType([value]);
          }}
        >
          <IndexTabsList>
            {FILE_TYPE_FILTERS.map((filter) => (
              <IndexTabTrigger key={filter.value} value={filter.value}>
                {filter.label}
              </IndexTabTrigger>
            ))}
          </IndexTabsList>
        </TabsGroup>
      </div>
      <RightMeta data-test="right-section-controls">
        {hasManageBaseTemplatesPermission && (
          <SecondaryButton
            data-test="creative-library-new-folder-button"
            size={BUTTON_SIZES.LARGE}
            onClick={() => {
              modals.create.toggle(true);
            }}
          >
            <FolderClosed />
            <span>{t('button.newFolder')}</span>
          </SecondaryButton>
        )}
        {!!flatSelectedFilters.length && (
          <GhostButton onClick={handleClearFilters} tabIndex={0} size={BUTTON_SIZES.LARGE}>
            {t('button.clearFilters')}
          </GhostButton>
        )}
        <CreativeLibraryFilterMenu
          onSelect={handleFilterChange}
          selectedFilters={flatSelectedFilters}
        />
        <WideInputSearch
          data-test="creative-library-search-input"
          id="creative-library-search-input"
          onChange={setSearchInputValue}
          placeholder={t('input.search.placeholder')}
          value={searchInputValue}
        />
        <StyledTableToggle
          activeView={activeView}
          data-test="creative-library-view-toggle"
          setActiveView={setActiveView}
          views={CREATIVE_LIBRARY_VIEWS}
        />
      </RightMeta>
    </ControlsContainer>
  );
};
